/**
 * Filling a party block from outside the user's typing — one code path for «Soy yo» (#81) and the
 * address book's suggestions (#159, spec D8).
 *
 * A party block is the name, the NIF and, on a goods DeCA, the address of one party: the goods
 * DeCA's Cargador and Transportista, the route sheet's Transportista and Contratante. «Soy yo» fills
 * one with the customer's own company and a suggestion fills one with a counterparty. Both write
 * the same columns the same way, and both ask with the same dialog before they replace what someone
 * typed. So they share this module rather than each keeping a copy that would drift.
 *
 * **What they do not share is when to ask**, and that stays with each caller:
 *
 *  - «Soy yo» asks whenever the block holds any text at all (`blockHasText`), as it always has.
 *  - A suggestion is chosen from inside one of the block's fields, and that field's text is the
 *    search itself, so it is replaced without asking. It asks only when *another* field holds text
 *    the choice would change (`replacesOtherText`).
 */

import { useState, type ReactNode } from 'react'
import type { FieldValues, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Dialog } from './Dialog.tsx'
import { Button } from './primitives.tsx'

/**
 * The columns this block names the party with — passed in, never derived from a section name.
 *
 * The two document types do not agree on them and must not be made to. A goods block is
 * `cargador_razon_social` / `_nif` / `_domicilio`; the route sheet's Transportista is
 * `transportista_nombre` and `transportista_nif` under Art. 2 of Orden FOM/1230/2013, with **no
 * address field at all**. Building the names by concatenation read plausibly and would have written
 * to columns that do not exist on one of the two forms, silently.
 */
export interface PartyColumns {
  name: string
  nif: string
  /** Absent where the document type has no address for this party — the route sheet. */
  address?: string
  /**
   * #219, a goods DeCA's shipper only: where its «Enviar DeCA a» and «Enviar automáticamente» live,
   * which choosing a saved entry from the book fills (`PartyCombobox`). «Soy yo» never does.
   */
  recipients?: { list: string; autoSend: string }
}

/**
 * One of a block's two searchable fields. The name or the NIF, never the address: the address
 * book is searched by who a party is, and an address alone names nobody.
 */
export interface PartyField {
  columns: PartyColumns
  field: 'name' | 'nif'
}

/** Which searchable field `column` is in this block, or `undefined` for its address. */
export function partyField(columns: PartyColumns, column: string): PartyField | undefined {
  if (column === columns.name) return { columns, field: 'name' }
  if (column === columns.nif) return { columns, field: 'nif' }
  return undefined
}

/**
 * What a block is filled with. The NIF and the address may be missing: a company record can lack
 * either, and a route sheet carries no address for anyone.
 */
export interface PartyValues {
  name: string
  nif?: string | null
  address?: string | null
}

type PartyForm = Pick<UseFormReturn<FieldValues>, 'getValues' | 'setValue'>

/** One column, written the way every fill writes: dirty, and validated so a stale mark goes. */
export function fillColumn(form: PartyForm, column: string, value: string): void {
  form.setValue(column, value, { shouldDirty: true, shouldValidate: true })
}

/**
 * The columns a fill writes, with what each gets: the name always, the NIF when there is one, and
 * the address only when the block has an address column and there is an address to put in it.
 * A missing value leaves its column alone rather than emptying it.
 */
function writesOf(columns: PartyColumns, values: PartyValues): [column: string, value: string][] {
  const writes: [string, string][] = [[columns.name, values.name]]
  if (values.nif) writes.push([columns.nif, values.nif])
  if (values.address && columns.address) writes.push([columns.address, values.address])
  return writes
}

/** Fills the block. Asking first, where that is due, is the caller's — see the module doc. */
export function writeParty(form: PartyForm, columns: PartyColumns, values: PartyValues): void {
  for (const [column, value] of writesOf(columns, values)) fillColumn(form, column, value)
}

function textOf(form: PartyForm, column: string): string {
  const value: unknown = form.getValues(column)
  return typeof value === 'string' ? value.trim() : ''
}

/** «Soy yo»'s rule: any of the block's fields holds text. */
export function blockHasText(form: PartyForm, columns: PartyColumns): boolean {
  return [columns.name, columns.nif, columns.address].some((column) => {
    if (!column) return false
    return textOf(form, column) !== ''
  })
}

/**
 * The suggestions' rule: a field other than `typedIn` holds text that the fill would change.
 *
 * Only the columns the fill writes are looked at, so a suggestion with no address never asks about
 * the address typed in the block: that address stays as it is. Text equal to what would be written
 * is not a replacement either, and asking about it would only teach people to click through.
 */
export function replacesOtherText(
  form: PartyForm,
  columns: PartyColumns,
  values: PartyValues,
  typedIn: string,
): boolean {
  return writesOf(columns, values).some(([column, value]) => {
    if (column === typedIn) return false
    const current = textOf(form, column)
    return current !== '' && current !== value.trim()
  })
}

/**
 * The question before a fill replaces typed text — «Soy yo»'s dialog and texts, with `company` the
 * name about to be written. `ask` opens it and runs `onConfirm` on «Sustituir»; cancelling, closing
 * or Escape write nothing. The caller renders `dialog` wherever it renders its own markup.
 */
export function useConfirmPartyOverwrite(): {
  ask: (name: string, onConfirm: () => void) => void
  dialog: ReactNode
} {
  const { t } = useTranslation()
  const [pending, setPending] = useState<{ name: string; onConfirm: () => void } | null>(null)

  const confirm = () => {
    setPending(null)
    pending?.onConfirm()
  }

  const dialog = (
    <Dialog
      open={pending !== null}
      title={t('create.soyYo.overwriteTitle')}
      subtitle={t('create.soyYo.overwriteBody')}
      onClose={() => setPending(null)}
      footer={
        <>
          <Button onClick={() => setPending(null)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={confirm}>
            {t('create.soyYo.overwriteConfirm')}
          </Button>
        </>
      }
    >
      <p>{t('create.soyYo.overwriteDetail', { company: pending?.name ?? '' })}</p>
    </Dialog>
  )

  return { ask: (name, onConfirm) => setPending({ name, onConfirm }), dialog }
}
